import { useState } from 'react'
import type { SavedDocument } from '@creanizer/types'
import { Button } from '@creanizer/ui'
import { Upload, AlertCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@creanizer/ui'

interface DocumentImporterProps {
  onImport: (documents: SavedDocument[]) => void
  maxCount?: number
}

export function DocumentImporter({ onImport, maxCount = 200 }: DocumentImporterProps) {
  const [isImporting, setIsImporting] = useState(false)
  const [error, setError] = useState<string>('')

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsImporting(true)
    setError('')

    try {
      const text = await file.text()
      const data = JSON.parse(text)

      // データ検証
      if (!Array.isArray(data)) {
        throw new Error('インポートファイルの形式が不正です')
      }

      // 文献データの検証
      const validDocuments: SavedDocument[] = []
      for (const item of data) {
        if (validateDocument(item)) {
          validDocuments.push(item)
        }
      }

      if (validDocuments.length === 0) {
        throw new Error('有効な文献データが見つかりませんでした')
      }

      if (validDocuments.length > maxCount) {
        throw new Error(`最大${maxCount}件までインポート可能です`)
      }

      // インポート実行
      onImport(validDocuments)
      
      // ファイル入力をリセット
      event.target.value = ''
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'インポートに失敗しました')
    } finally {
      setIsImporting(false)
    }
  }

  // 文献データの検証
  const validateDocument = (item: any): boolean => {
    if (!item.id || typeof item.id !== 'string') return false
    if (!item.title || typeof item.title !== 'string') return false
    if (!item.input || typeof item.input !== 'object') return false
    if (!item.result || typeof item.result !== 'object') return false
    if (!item.result.type || typeof item.result.type !== 'string') return false
    if (!item.createdAt || typeof item.createdAt !== 'string') return false
    
    return true
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-4">
        <input
          type="file"
          accept=".json"
          onChange={handleFileSelect}
          disabled={isImporting}
          className="hidden"
          id="document-import-input"
        />
        <label htmlFor="document-import-input">
          <Button
            disabled={isImporting}
            variant="outline"
            className="flex items-center gap-2 cursor-pointer"
          >
            <Upload className="w-4 h-4" />
            {isImporting ? 'インポート中...' : '文献をインポート'}
          </Button>
        </label>
        
        <span className="text-sm text-muted-foreground">
          JSON形式のファイルをインポートできます
        </span>
      </div>
    </div>
  )
}